import type { BioBanks } from '../types.js';

/**
 * French bio material for BeurteletChatFun synthetic profiles.
 *
 * Written the way French 20-somethings actually fill a bio: short, mostly
 * lowercase, fragments, little punctuation, a bit of verlan and texting
 * shorthand. Every bank is shared across genders, so lines avoid gendered
 * agreement ("fatigué·e") — they read naturally for anyone. No emoji (the
 * engine adds them), no platform names, no handles.
 */

const FUNNY: string[] = [
  'je dors plus que mon chat et lui au moins il a une excuse',
  'mon passe-temps préféré c\'est annuler des plans',
  'je cuisine pas mais je commente très bien',
  'en couple avec mon lit depuis des années',
  'mon niveau de sport : courir après le bus',
  'le régime commence lundi. quel lundi, on sait pas',
  'je réponds sous 3 à 5 jours ouvrés',
  'mes deux passions : {hobby} et la sieste',
  'je viens de {city}, donc forcément je râle',
  '{zodiac}, donc oui c\'est compliqué',
  'j\'ai l\'humour d\'un tonton au repas de famille',
  'ma mère pense que je suis drôle, c\'est déjà ça',
  'je mets 45 min à choisir un film et je dors au bout de 10',
  'batterie sociale à 3%',
  'pro du "j\'arrive dans 5 min" (spoiler : non)',
  'je fais semblant de comprendre le hors-jeu pour avoir la paix',
  'tous mes plans tombent à l\'eau sauf les plans kebab',
  'si tu me cherches je suis devant le frigo',
  'abonnement à la salle depuis janvier, j\'y ai mis les pieds deux fois',
  'j\'ai raté ma vocation de chat',
  'on m\'a dit d\'écrire une bio. voilà',
  'bio en travaux, repassez plus tard',
  'je viens, je vois, je fais la sieste',
  'plus de photos de bouffe que de selfies dans ma galerie',
  'je parle à mes plantes et elles meurent quand même',
  'mon sens de l\'orientation a démissionné',
  'je ris à mes propres blagues, faut bien quelqu\'un',
  'jamais à l\'heure mais toujours avec des croissants',
  'si je dis j\'arrive, compte 20 minutes',
  'spécialiste du câlin et du grignotage',
  'doctorat en procrastination, mention très bien',
  'accro à {hobby}, en désintox depuis jamais',
  'je parle trois langues : français, sarcasme et vocaux de 4 min',
  'j\'envoie des vocaux de 6 minutes, pardon d\'avance',
  'mon plat préféré c\'est celui des autres',
  'le café c\'est littéralement ma personnalité',
  'je connais tous les dialogues de Shrek par cœur',
  'j\'ai 12 onglets ouverts dans ma tête',
  'si la flemme était un sport je serais en équipe de France',
  'team pas de réveil le dimanche',
  '{trait} et toujours la dalle',
  'je pleure devant les pubs de Noël',
  'j\'ai jamais gagné au Monopoly et je le vis mal',
  'je chante faux mais fort',
  'mon chat a plus de vie sociale que moi',
  'j\'ai arrêté le sport, il me ralentissait',
  'je fais des listes de choses à faire et je les perds',
  'spécialité : commander des tacos à 2h du mat',
  'ma playlist est un crime contre l\'humanité',
  'si t\'aimes pas les frites on peut pas parler',
  'je regarde des vidéos de cuisine en mangeant des pâtes',
  'mon talent caché : m\'endormir n\'importe où',
  'j\'ai mis 3 heures à écrire ça',
  'je suis pas en retard, les autres sont en avance',
  '{city} > le reste du monde, débat interdit',
  'je me couche tôt (mensonge)',
  'mon boulanger connaît mon prénom et ma commande',
  'j\'ai des problèmes de confiance avec les escaliers',
  'zéro en drague, dix sur dix en raclette',
  'j\'ai binge-watché trop de séries pour mon propre bien',
  'pro de la sieste de 3 heures',
  'mon seul sport : zapper les pubs',
  'le dimanche c\'est pyjama, non négociable',
  'je réponds vite seulement si tu parles de bouffe',
  'sarcasme niveau expert, cuisine niveau débutant',
  'je parle plus à mon chien qu\'aux humains',
  'on se connaît pas mais j\'ai déjà un avis',
  'un jour je ferai du yoga. pas aujourd\'hui',
  'mémoire de poisson rouge, rappelle-moi ton prénom',
  'j\'ai tenté la cuisine healthy, j\'ai fini au grec',
  'insomnie niveau pro',
  'je lis l\'horoscope pour savoir qui éviter',
  '{zodiac} ascendant pizza',
  'dans une relation sérieuse avec {hobby}',
  '{job} le jour, critique de séries la nuit',
  '{school} et déjà besoin de vacances',
  'fan de {team} donc j\'ai l\'habitude de souffrir',
  'je mange des céréales à toute heure',
  'si tu ramènes du fromage on est amis',
  'ma grand-mère dit que je suis quelqu\'un de bien, ça compte',
  'je parle fort, c\'est génétique',
  'j\'ai un rire bizarre, tu vas voir',
  'trop de pulls, pas assez de placards',
  'mon rêve : une sieste sans réveil',
  'deux vitesses : sieste et chaos',
  'j\'ai perdu mes clés en écrivant cette bio',
  'chef étoilé des pâtes au beurre',
  'le seul cardio que je fais c\'est stresser',
  'j\'ai un avis sur tout, même sur les nuages',
  'je rate jamais un apéro',
  'mon horoscope dit que tu vas me parler',
  'je dis oui à tout sauf au réveil',
  'on peut parler de tout sauf de mon compte en banque',
  'pas de bio, juste des vibes',
  'je fais la meilleure raclette de {city}',
  'je mange épicé et je pleure en silence',
  'je confonds gauche et droite depuis toujours',
  'ma spécialité : rire au mauvais moment',
  'ma daronne valide pas cette bio',
  'je fais des crêpes pour me faire pardonner',
  'mon frigo est vide mais mon cœur est plein',
  'je dis "dernier épisode" depuis 4 heures',
];

const CATCHY: string[] = [
  'ici pour les bonnes vibes',
  'good vibes only',
  'simple mais pas basique',
  'vis ta vie',
  'toujours le sourire',
  'pas de prise de tête',
  'le soleil dans le cœur',
  'ici pour rire',
  'un café et ça repart',
  '{city} dans le cœur',
  'made in {city}',
  '{city} for life',
  '{zodiac} jusqu\'au bout',
  '{team} jusqu\'au bout',
  'ici c\'est {city}',
  'la famille avant tout',
  'le respect avant tout',
  'vrai ou rien',
  'zéro filtre',
  'on avance',
  'jamais sans mes écouteurs',
  'musique, café, liberté',
  '{hobby}, musique, soleil',
  'chill et bienveillance',
  'les gens vrais seulement',
  'ici pour discuter',
  'en mode avion',
  'dans ma bulle',
  'sourire c\'est gratuit',
  'la vie est trop courte pour les mauvaises vibes',
  'un pied dans la lune',
  'tête en l\'air, cœur sur la main',
  'la perfection c\'est surfait',
  'le calme avant la tempête',
  'un billet d\'avion et je suis là',
  'voyager, rire, recommencer',
  'soleil, mer et musique',
  'entre {city} et ailleurs',
  'au jour le jour',
  'doucement mais sûrement',
  'on lâche rien',
  'mes proches avant tout',
  'la nuit je vis',
  'rires garantis',
  'humour douteux garanti',
  '{trait} avec un grand cœur',
  '{hobby} + café = bonheur',
  'allergique aux gens fake',
  'ici pour les vraies discussions',
  'parle-moi de ta musique préférée',
  'dis-moi ton plat préféré',
  'raconte-moi ton dernier voyage',
  'dis-moi ton signe, je te dis qui tu es',
  'ton film préféré, go',
  'tu kiffes {hobby} ? on va s\'entendre',
  'écris-moi si t\'aimes {hobby}',
  'envoie ta meilleure blague',
  'une vibe, une vie',
  'le sourire avant tout',
  'âme d\'artiste',
  'rêver grand',
  'sobre et efficace',
  'peu de mots, beaucoup de rires',
  'ma playlist parle pour moi',
  'la mer me manque',
  'le monde est à nous',
  'pas de stress, que de l\'amour',
  'ici pour l\'ambiance',
  'bonne humeur obligatoire',
  'énergie positive',
  'l\'esprit libre',
  'chaque jour compte',
  'doucement le matin',
  'vivre et laisser vivre',
  'rien à prouver',
  'ici pour les fous rires',
  'ambiance chill',
  'pyjama et séries',
  '{job} avec le sourire',
  '{school} et bon courage à moi',
  '{team} ou rien',
  '{team} toujours',
  '{city} et mes racines avant tout',
  'entre deux cultures, un seul cœur',
  'le bled l\'été, {city} l\'hiver',
  'le bled me manque',
  'thé à la menthe et bonne humeur',
  'couscous du dimanche, c\'est sacré',
  'ici c\'est Paris et nulle part ailleurs',
  'vibes du sud',
  'accent du sud, cœur du sud',
  'le nord dans le cœur',
  'la vie est belle quand on la rend belle',
  'petit à petit',
  'dans le doute, souris',
  'calme dehors, tempête dedans',
  'ici pour faire des rencontres sympas',
  'pas là pour plaire, là pour parler',
  'la nuit, les étoiles, la musique',
  'karaoké quand tu veux',
  'toujours un son dans la tête',
  '{hobby} et rien d\'autre',
];

const CRINGE: string[] = [
  'à la recherche de mon âme sœur',
  'si tu lis ça c\'est le destin',
  'je cherche quelqu\'un qui me fera oublier mon téléphone',
  'ma vie est un film et je cherche ma co-star',
  'les vrais savent',
  'pas comme les autres',
  'trop d\'amour à donner',
  'mon cœur est fermé pour travaux',
  'le passé m\'a appris, le futur m\'attend',
  'je suis un livre ouvert, à toi de lire',
  'certains m\'aiment, d\'autres m\'envient',
  'je suis ce que je suis, prends-moi comme ça',
  'si tu me veux, mérite-moi',
  'mon sourire cache beaucoup de choses',
  'les gens changent, les souvenirs restent',
  'ne me juge pas, tu connais pas mon histoire',
  'mon cœur est trop grand pour ce monde',
  'la loyauté avant tout, le reste suit',
  'ma seule faiblesse c\'est le chocolat',
  'tu cherches la perfection ? arrête, je suis là',
  'cœur de pierre mais âme d\'enfant',
  'le bonheur se cache dans les petites choses',
  'je crois encore au coup de foudre',
  'à la recherche de ma moitié',
  'qui m\'aime me suive',
  'un regard suffit',
  'on se reconnaîtra',
  'toi + moi = ?',
  'ici pour trouver la perle rare',
  'le destin nous a réunis ici',
  'laisse ton cœur parler',
  'mes yeux disent tout',
  '{zodiac} : c\'est écrit dans les étoiles',
  '{zodiac} donc je ressens tout x1000',
  'les {zodiac} sont les meilleurs, c\'est prouvé',
  'fan de {team}, si t\'aimes pas passe ton chemin',
  '{city} c\'est ma vie, mon cœur, mon tout',
  'roi du drame, reine du pardon',
  'je suis la pièce manquante de ton puzzle',
  'ma vie, mes règles',
  'moi c\'est moi, toi c\'est toi',
  'je donne tout ou rien',
  'tu me cherches ? me voilà',
  'un jour tu comprendras',
  'personne me connaît vraiment',
  'je pardonne mais j\'oublie pas',
  'les apparences sont trompeuses',
  'derrière ce sourire il y a une histoire',
  'mon cœur bat pour ceux qui le méritent',
  'je vis, je rêve, j\'aime',
  'l\'amour c\'est pour les courageux',
  'je cherche pas, je trouve',
  'si t\'as pas d\'humour, passe',
  'mon ex dit que je suis une bonne personne, à toi de voir',
  'je cherche quelqu\'un pour partager mes frites',
  'le bonheur c\'est maintenant',
  'tu seras peut-être la raison de mon sourire',
  'à toi qui lis ma bio : bonne journée',
  'si tu me fais rire t\'as gagné',
  'ma maman m\'a dit de pas parler aux inconnus, donc présente-toi',
  'je suis la définition du mot loyauté',
  'cherche quelqu\'un qui aime {hobby} autant que moi',
  'fan de {hobby}, à la recherche de mon binôme',
  '{trait} mais au fond un grand cœur',
  'j\'ai le cœur sur la main et la main dans le paquet de chips',
  'je suis rare, profites-en',
  'personne n\'est parfait, mais j\'essaie',
  'une rose parmi les épines',
  'je brille même dans le noir',
  'chaque jour est un nouveau départ',
  'le cœur a ses raisons',
  'mon cœur est ma boussole',
  'tu me plais déjà, je le sens',
  'que des bonnes ondes, les jaloux dehors',
  'les jaloux vont dire que c\'est faux',
  'quand je veux quelque chose, je l\'obtiens',
  'je mérite le meilleur et toi aussi',
  'une âme, une flamme',
  'tu vas tomber amoureux de mes vocaux',
  'je fais pas semblant, moi',
  'trop vrai pour ce monde',
  'je suis un cadeau, faut juste savoir m\'ouvrir',
  'la vie est un voyage, monte avec moi',
  'mon cœur parle plusieurs langues',
  'je suis là pour écrire notre histoire',
  'tu cherchais quelqu\'un comme moi ? c\'est fait',
  'si tu crois au destin, écris-moi',
  'mon cœur est un jardin, prends-en soin',
  'je crois aux âmes sœurs, et toi ?',
  'tu peux pas m\'oublier, essaie',
  'je suis le soleil de quelqu\'un',
  'toujours fidèle à moi-même',
  'je vis pour ces petits moments',
  'c\'est les étoiles qui m\'envoient',
  'mon rire est contagieux, attention',
  'les gens comme moi ça se compte sur les doigts',
  'un cœur d\'or dans un monde de pierre',
  'j\'ai grandi à {city}, maintenant je brille partout',
  '{job} le jour, la tête dans les étoiles la nuit',
  'les meilleures histoires commencent par un salut',
];

const SINCERE: string[] = [
  'je viens d\'arriver sur l\'appli, soyez indulgents',
  'ici pour discuter et rencontrer du monde',
  'j\'aime les discussions longues et vraies',
  'un peu timide au début, après ça va',
  'j\'écoute plus que je parle',
  'j\'aime les gens simples et honnêtes',
  'je cherche juste des gens avec qui parler le soir',
  'la famille compte énormément pour moi',
  'j\'aime rire, manger et voyager',
  'je préfère les vocaux aux longs messages',
  'je réponds pas toujours vite mais je réponds',
  'je découvre des sons tous les jours, partagez les vôtres',
  'les bios c\'est pas mon truc, on discute plutôt',
  'je bosse beaucoup, alors le soir j\'aime papoter',
  'la musique m\'aide à tenir',
  'je suis quelqu\'un de calme, je déteste les embrouilles',
  'j\'aime les gens qui assument qui ils sont',
  'si tu veux parler, je suis là',
  'nouveau départ dans une nouvelle ville, je connais personne',
  'je cherche des gens pour chanter le soir',
  'j\'adore les débats tant qu\'on reste respectueux',
  'je fais de mon mieux tous les jours',
  'j\'essaie de prendre soin de moi cette année',
  'le respect, c\'est la base pour moi',
  'le soir c\'est calme chez moi, alors je passe ici',
  'j\'aime bien rencontrer des gens de partout',
  'je suis là pour passer un bon moment, rien de plus',
  'j\'aime parler de tout, surtout de musique',
  'j\'ai grandi entre deux cultures et c\'est une richesse',
  'mes amis disent que je suis de bon conseil',
  'j\'aime les soirées calmes à refaire le monde',
  'pas de prise de tête, juste de la bonne humeur',
  'je cherche des gens bienveillants',
  'j\'adore cuisiner pour les autres',
  'je passe mes soirées à écouter de la musique',
  'je suis du genre à écouter les problèmes des autres',
  'honnête avant tout, même quand ça fait mal',
  'j\'aime apprendre de nouvelles choses',
  'je parle français et un peu d\'arabe',
  'je parle français, arabe et un peu d\'anglais',
  'j\'aime les vrais échanges, pas les conversations bateau',
  'j\'ai un faible pour les gens drôles',
  'je suis en reconversion, pas facile mais ça avance',
  'je travaille de nuit, donc je suis là tard',
  'je profite de chaque moment avec mes proches',
  'j\'aime beaucoup les animaux, surtout les chats',
  'j\'aime chanter même si je chante mal',
  'je cherche des gens avec qui faire des lives',
  'je suis là pour l\'ambiance des salons',
  'j\'aime écouter les autres chanter',
  'les salons du soir, c\'est mon moment',
  'j\'ai besoin de temps pour m\'ouvrir mais ça vaut le coup',
  'l\'écrit c\'est pas mon fort, je suis plus à l\'aise à l\'oral',
  'je me remets au sport doucement',
  'j\'aime marcher des heures en écoutant de la musique',
  'je fais attention aux autres, j\'aime qu\'on fasse pareil',
  'je cherche des amis avant tout',
  'j\'aime les conversations qui durent jusqu\'au matin',
  'je suis quelqu\'un de simple',
  'j\'aime voyager, même pas loin',
  'je suis souvent dans mes pensées',
  'la gentillesse, c\'est ce qui compte le plus pour moi',
  'je suis là pour sortir un peu de ma routine',
  'je déteste le mensonge',
  'je suis fan de cinéma, on peut en parler des heures',
  'je lis beaucoup, conseillez-moi des livres',
  'j\'aime prendre le temps de connaître les gens',
  'un peu sur la réserve mais très à l\'écoute',
  'j\'aime les gens qui sourient facilement',
  'je cherche des personnes avec qui parler sans jugement',
  'j\'aime partager mes découvertes musicales',
  'je viens ici pour me changer les idées',
  'j\'ai un grand cœur mais je le montre pas tout de suite',
  'j\'aime rendre service',
  'je ne juge personne, chacun son histoire',
  'les week-ends en famille, ça n\'a pas de prix',
  'j\'aime les fous rires entre amis',
  'je passe beaucoup de temps avec mes neveux',
  'j\'aime la nature et le calme',
  'la mer me ressource',
  'je suis de nature joyeuse',
  'j\'aime quand les gens sont directs',
  'je préfère la qualité à la quantité, en amitié aussi',
  'je fais des études et je bosse à côté, je dors pas beaucoup',
  'je suis rarement en ligne mais ça me fait plaisir de discuter',
  'la curiosité c\'est mon moteur',
  'j\'aime découvrir d\'autres cultures',
  'mes racines comptent beaucoup pour moi',
  'je cherche juste un peu de compagnie le soir',
  'merci de rester respectueux',
  'les discussions profondes me manquent',
  'je suis quelqu\'un de fidèle en amitié',
];

const HOBBIES: string[] = [
  'musique', 'séries', 'films', 'mangas', 'animés', 'muscu', 'foot', 'basket', 'boxe', 'MMA',
  'running', 'natation', 'vélo', 'randonnée', 'escalade', 'yoga', 'pilates', 'danse', 'zumba', 'fitness',
  'cuisine', 'pâtisserie', 'brunch', 'café', 'thé', 'restos', 'street food', 'barbecue', 'chicha', 'apéro',
  'voyages', 'road trips', 'plage', 'montagne', 'ski', 'snowboard', 'surf', 'camping', 'nature', 'balades',
  'lecture', 'romans', 'BD', 'poésie', 'écriture', 'journal intime', 'philo', 'histoire', 'astronomie', 'astrologie',
  'photo', 'vidéo', 'montage', 'dessin', 'peinture', 'tatouages', 'piercings', 'mode', 'shopping', 'friperie',
  'maquillage', 'skincare', 'ongles', 'coiffure', 'parfums', 'sneakers', 'streetwear', 'déco', 'plantes', 'jardinage',
  'jeux vidéo', 'gaming', 'FIFA', 'Fortnite', 'Valorant', 'LoL', 'Mario Kart', 'Pokémon', 'jeux de société', 'échecs',
  'cartes', 'karaoké', 'chant', 'rap', 'rap français', 'RnB', 'raï', 'afro', 'reggaeton', 'zouk',
  'électro', 'techno', 'rock', 'pop', 'jazz', 'classique', 'variété française', 'chanson française', 'guitare', 'piano',
  'batterie', 'DJ', 'concerts', 'festivals', 'théâtre', 'stand-up', 'cinéma', 'expos', 'musées', 'opéra',
  'podcasts', 'vlogs', 'true crime', 'documentaires', 'téléréalité', 'k-drama', 'k-pop', 'Disney', 'Harry Potter', 'Star Wars',
  'Marvel', 'science-fiction', 'fantasy', 'horreur', 'thrillers', 'comédies romantiques', 'sieste', 'grasse mat', 'soirées', 'boîte',
  'chats', 'chiens', 'chevaux', 'animaux', 'bénévolat', 'associatif', 'langues', 'anglais', 'espagnol', 'japonais',
  'coréen', 'arabe', 'calligraphie', 'couture', 'tricot', 'crochet', 'bricolage', 'mécanique', 'voitures', 'motos',
  'karting', 'pétanque', 'tennis', 'padel', 'badminton', 'ping-pong', 'volley', 'handball', 'rugby', 'judo',
  'karaté', 'taekwondo', 'kick-boxing', 'équitation', 'patinage', 'roller', 'skate', 'trottinette', 'paddle', 'plongée',
  'pêche', 'astronomie amateur', 'tech', 'code', 'crypto', 'investissement', 'entrepreneuriat', 'développement perso', 'méditation', 'spiritualité',
  'sport', 'balades de nuit', 'couchers de soleil', 'marchés', 'brocantes', 'vinyles', 'cuisine marocaine', 'cuisine algérienne', 'pâtisseries orientales', 'thé à la menthe',
];

const TRAITS: string[] = [
  'calme', 'drôle', 'timide', 'sociable', 'sensible', 'solitaire', 'sincère', 'honnête', 'fidèle', 'lunatique',
  'sympa', 'cash', 'simple', 'tranquille', 'aimable', 'chill', 'zen', 'speed', 'relou', 'optimiste',
  'pessimiste', 'réaliste', 'idéaliste', 'perfectionniste', 'romantique', 'nostalgique', 'sarcastique', 'ironique', 'pudique', 'susceptible',
  'adorable', 'imprévisible', 'unique', 'dynamique', 'énergique', 'autonome', 'responsable', 'fiable', 'modeste', 'humble',
  'sauvage', 'tête en l\'air', 'terre à terre', 'oiseau de nuit', 'couche-tard', 'lève-tôt', 'dans la lune', 'à l\'ouest', 'bon public', 'pipelette',
  'accro au café', 'accro au sucre', 'fan de câlins', 'pot de colle', 'casse-pieds', 'grande gueule', 'cœur d\'artichaut', 'cœur sur la main', 'tête de mule', 'tête brûlée',
  'boute-en-train', 'comique', 'empathique', 'altruiste', 'philosophe', 'artiste', 'poète', 'geek', 'nerd', 'maniaque',
  'bordélique', 'toujours en retard', 'toujours à l\'heure', 'toujours de bonne humeur', 'jamais d\'accord', 'sans filtre', 'sans prise de tête', 'franc-parler', 'à fleur de peau', 'au taquet',
  'en mode chill', 'facile à vivre', 'insomniaque', 'workaholic', 'foodie', 'gamer', 'otaku', 'cinéphile', 'mélomane', 'solaire',
  'rêves plein la tête', 'sérievore', 'gourmandise assumée', 'team canapé', 'âme vagabonde', 'esprit libre', 'esprit de famille', 'grand cœur', 'bulle de calme', 'zéro jugement',
  'fidèle en amitié', 'pipelette le soir', 'nocturne', 'lève-tôt même le week-end', 'spontanéité', 'hypersensible', 'système D', 'agenda à jour', 'curiosité', 'patience d\'ange',
];

export const FR_BIO: BioBanks = {
  oneLiners: { funny: FUNNY, catchy: CATCHY, cringe: CRINGE, sincere: SINCERE },
  hobbies: HOBBIES,
  traits: TRAITS,
  cities: [
    { name: 'Paris', weight: 30 }, { name: 'Marseille', weight: 18 }, { name: 'Lyon', weight: 16 },
    { name: 'Toulouse', weight: 10 }, { name: 'Lille', weight: 10 }, { name: 'Nice', weight: 8 },
    { name: 'Nantes', weight: 8 }, { name: 'Montpellier', weight: 8 }, { name: 'Bordeaux', weight: 8 },
    { name: 'Strasbourg', weight: 7 }, { name: 'Rennes', weight: 5 }, { name: 'Saint-Étienne', weight: 4 },
    { name: 'Toulon', weight: 4 }, { name: 'Grenoble', weight: 4 }, { name: 'Saint-Denis', weight: 4 },
    { name: 'Reims', weight: 3 }, { name: 'Le Havre', weight: 3 }, { name: 'Dijon', weight: 3 },
    { name: 'Angers', weight: 3 }, { name: 'Nîmes', weight: 3 }, { name: 'Montreuil', weight: 3 },
    { name: 'Roubaix', weight: 3 }, { name: 'Mulhouse', weight: 3 }, { name: 'Metz', weight: 3 },
    { name: 'Rouen', weight: 3 }, { name: 'Clermont-Ferrand', weight: 3 }, { name: 'Tours', weight: 3 },
    { name: 'Villeurbanne', weight: 2 }, { name: 'Argenteuil', weight: 2 }, { name: 'Perpignan', weight: 2 },
    { name: 'Avignon', weight: 2 }, { name: 'Créteil', weight: 2 }, { name: 'Nanterre', weight: 2 },
    { name: 'Aubervilliers', weight: 2 }, { name: 'Limoges', weight: 2 }, { name: 'Amiens', weight: 2 },
    { name: 'Orléans', weight: 2 }, { name: 'Cergy', weight: 2 }, { name: 'Sarcelles', weight: 2 },
    { name: 'Bobigny', weight: 2 }, { name: 'Vénissieux', weight: 1 }, { name: 'Aix-en-Provence', weight: 2 },
  ],
  teams: [
    'PSG', 'l\'OM', 'l\'OL', 'le LOSC', 'le Stade Rennais', 'l\'ASSE', 'le RC Lens', 'l\'OGC Nice',
    'le FC Nantes', 'les Girondins', 'le TFC', 'le Racing', 'Montpellier', 'Brest', 'Monaco', 'le Stade de Reims',
    'le Real', 'le Barça', 'Liverpool', 'Arsenal', 'l\'Algérie', 'le Maroc', 'la Tunisie', 'les Bleus',
    'le Stade Toulousain', 'le Sénégal',
  ],
  schools: [
    'en L3 psycho', 'en M1 droit', 'en BTS MCO', 'en BTS NDRC', 'en prépa', 'à la Sorbonne', 'à Sciences Po', 'à l\'IUT',
    'en fac de droit', 'en STAPS', 'en LEA', 'en école de commerce', 'en IFSI', 'en alternance', 'en BUT GEA', 'en BUT info',
    'en licence d\'anglais', 'en master marketing', 'en médecine', 'en PASS', 'en école d\'ingé', 'à la fac de Nanterre', 'à Paris 8', 'à Lyon 2',
    'à Aix-Marseille', 'à l\'INSA', 'en CAP coiffure', 'en bac pro commerce', 'en BTS SIO', 'en socio', 'en L2 éco-gestion', 'en master RH',
    'en école de design', 'en L1 info', 'en BTS compta', 'en fac de langues',
  ],
  jobs: [
    'prof', 'prof des écoles', 'prof de sport', 'kiné', 'dentiste', 'médecin', 'interne en médecine', 'orthophoniste',
    'psychologue', 'sage-femme', 'auxiliaire de puériculture', 'aide à domicile', 'AESH', 'éduc spé', 'pompier', 'militaire',
    'gendarme', 'agent de sécurité', 'agent SNCF', 'VTC', 'cariste', 'mécano', 'cuisto', 'barista',
    'dev', 'dev web', 'data analyst', 'community manager', 'UX designer', 'chef de projet', 'graphiste', 'photographe',
    'architecte', 'journaliste', 'libraire', 'fleuriste', 'secrétaire', 'comptable', 'RH', 'juriste',
    'notaire', 'stagiaire', 'freelance', 'fonctionnaire', 'PNC', 'mannequin', 'styliste', 'make-up artist',
    'coach sportif', 'manager', 'agent immo', 'standardiste',
  ],
  lyricsOrQuotes: [
    'on lâche rien',
    'qui vivra verra',
    'petit à petit l\'oiseau fait son nid',
    'après la pluie le beau temps',
    'c\'est la vie',
    'carpe diem',
    'hakuna matata',
    'la vie en rose',
    'non, je ne regrette rien',
    'mektoub',
    'la famille avant tout',
    'on n\'a qu\'une vie',
    'dans la légende',
    'le monde ou rien',
    'il en faut peu pour être heureux',
    'libérée, délivrée',
    'chacun sa route, chacun son chemin',
    'ça plane pour moi',
    'tout le bonheur du monde',
    'la vie est un long fleuve tranquille',
    'je ne suis pas un héros',
    'à la vie à la mort',
    'seul on va plus vite, ensemble on va plus loin',
    'on se refait pas',
    'la nuit porte conseil',
    'qui ne tente rien n\'a rien',
    'mieux vaut tard que jamais',
    'ma liberté de penser',
    'on ira où tu voudras quand tu voudras',
    'c\'est basique, simple',
    'tout est écrit',
    'bella ciao',
  ],
  pets: [
    'deux chats', 'un chat roux', 'un chat qui me méprise', 'un berger australien', 'un shiba très drama',
    'un lapin nain', 'un golden retriever', 'un chihuahua caractériel', 'trois chats et zéro regret', 'un bouledogue français',
    'un husky qui hurle tout le temps', 'un perroquet bavard', 'un hamster nommé Biscotte', 'un chaton de 3 mois', 'un malinois',
    'un chat noir porte-bonheur', 'deux tortues', 'un poisson rouge nommé Nemo', 'un chat nommé Pixel', 'un chien nommé Oslo',
    'un staffie adorable', 'un cocker', 'un chat sphynx', 'un furet',
  ],
  zodiac: {
    aries: 'Bélier', taurus: 'Taureau', gemini: 'Gémeaux', cancer: 'Cancer', leo: 'Lion', virgo: 'Vierge',
    libra: 'Balance', scorpio: 'Scorpion', sagittarius: 'Sagittaire', capricorn: 'Capricorne', aquarius: 'Verseau', pisces: 'Poissons',
  },
  topicTemplates: {
    zodiac: ['{zodiac}', '{zodiac} ascendant chaos', 'très {zodiac}', '{zodiac} donc c\'est normal', 'typique {zodiac}', '{zodiac}, tu connais la suite'],
    team: ['{team}', 'fan de {team}', '{team} avant tout', '{team} dans le sang', 'supporter de {team} depuis petit', 'allez {team}'],
    school: ['{school}', 'actuellement {school}', '{school} (oui je souffre)', '{school} cette année', '{school} à {city}'],
    job: ['{job}', '{job} dans la vie', 'métier : {job}', '{job} à {city}', '{job} le jour, {trait} la nuit'],
    hobby: ['fan de {hobby}', '{hobby} h24', 'team {hobby}', '{hobby} et rien d\'autre', 'grosse passion : {hobby}', '{hobby} le week-end'],
    lyric: ['{lyric}', '« {lyric} »', '{lyric} comme on dit', '"{lyric}"'],
    oneLiner: ['{oneLiner}', '{oneLiner} voilà', '{oneLiner} bref'],
    height: ['{height}', '{height} sans talons', 'je fais {height}', '{height} si ça compte'],
    pet: ['{pet} à la maison', 'coloc avec {pet}', 'je vis avec {pet}', 'humain de {pet}'],
  },
  formatHeight: (cm) => `${String(Math.floor(cm / 100))}m${String(cm % 100).padStart(2, '0')}`,
  emojis: {
    general: [
      '✨', '🌙', '☀️', '🎧', '🎶', '📚', '☕', '🌸', '🔥', '💫',
      '🌊', '🍕', '🎮', '📸', '✈️', '🌴', '🦋', '🖤', '🤍', '😴',
      '🥐', '🍫', '🎬', '🧿', '🌹', '🎤', '🍃', '😎', '🫶', '🌅',
    ],
    female: ['🌸', '🦋', '💅', '🎀', '🌷', '💗', '🧸', '🌺'],
    male: ['⚽', '🏀', '🏎️', '💪', '🎮', '🦁', '🔥', '🏍️'],
  },
  imperfections: {
    substitutions: [
      [/\bje suis\b/gi, 'jsuis'],
      [/\bje (?:ne )?sais pas\b/gi, 'jsp'],
      [/\bt'inquiète\b/gi, 'tkt'],
      [/\bmort de rire\b/gi, 'mdr'],
      [/\bquelque chose\b/gi, 'qqch'],
      [/\bpourquoi\b/gi, 'pk'],
      [/\bbeaucoup\b/gi, 'bcp'],
      [/\bs'il te plaît\b/gi, 'stp'],
      [/\btout le monde\b/gi, 'tlm'],
      [/\btoujours\b/gi, 'tjrs'],
      [/\bje vais\b/gi, 'jvais'],
      [/\bil y a\b/gi, 'ya'],
      [/\bje pense\b/gi, 'jpense'],
    ],
    lowercaseRate: 0.5,
    dropDiacriticsRate: 0.3,
    noFinalPunctRate: 0.7,
  },
  genderTopicBias: {
    female: { zodiac: 2.2, pet: 1.6, lyric: 1.3, height: 0.4 },
    male: { team: 2.2, height: 1.6, job: 1.3, zodiac: 0.6 },
  },
};
